import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './config';
import type { 
  MenuSection, 
  MenuItem, 
  MenuSectionFormData,
  DietType,
  Restaurant,
  AllergenInfo,
  SpiceLevel,
  CuisineTag,
  CustomDietaryTag,
  ImageMetadata
} from '@/types';

// Interface for creating a new menu item
interface MenuItemCreateData {
  name: string;
  description?: string;
  price: number;
  dietType: DietType;
  sectionId: string;
  restaurantId: string;
  order: number;
  isAvailable: boolean;
  isPopular: boolean;
  isNew: boolean;
  isVegan?: boolean;
  isJain?: boolean;
  preparationTime?: number;
  calories?: number;
  ingredients?: string[];
  allergens?: string[];
  spiceLevel?: 1 | 2 | 3 | 4 | 5;
  // Enhanced diet type fields
  enhancedAllergens?: AllergenInfo;
  enhancedSpiceLevel?: SpiceLevel;
  cuisineTags?: CuisineTag[];
  customTags?: CustomDietaryTag[];
  isSignatureDish?: boolean;
  isChefSpecial?: boolean;
  imageUrl?: string;
  imageMetadata?: ImageMetadata;
  originalPrice?: number;
}

// Interface for updating a menu item (all fields optional except structural ones)
interface MenuItemUpdateData {
  name?: string;
  description?: string;
  price?: number;
  dietType?: DietType;
  sectionId?: string;
  isAvailable?: boolean;
  isPopular?: boolean;
  isNew?: boolean;
  isVegan?: boolean;
  isJain?: boolean;
  preparationTime?: number;
  calories?: number;
  ingredients?: string[];
  allergens?: string[];
  spiceLevel?: 1 | 2 | 3 | 4 | 5;
  // Enhanced diet type fields
  enhancedAllergens?: AllergenInfo;
  enhancedSpiceLevel?: SpiceLevel;
  cuisineTags?: CuisineTag[];
  customTags?: CustomDietaryTag[];
  isSignatureDish?: boolean;
  isChefSpecial?: boolean;
  imageUrl?: string;
  imageMetadata?: ImageMetadata;
  originalPrice?: number;
}

// Simple interface for menu data returned by getMenu
interface MenuData {
  sections: MenuSection[];
  items: MenuItem[];
  isPublished: boolean;
}

// Collection references
export const sectionsCollection = collection(db, 'menuSections');
export const itemsCollection = collection(db, 'menuItems');

/**
 * Get complete menu for a restaurant
 */
export const getMenu = async (restaurantId: string): Promise<MenuData | null> => {
  try {
    // For new restaurants, we'll just use simple where queries without orderBy
    // This avoids the composite index requirement when there are no documents
    const sectionsQuery = query(
      sectionsCollection,
      where('restaurantId', '==', restaurantId)
    );
    const sectionsSnapshot = await getDocs(sectionsQuery);
    
    const itemsQuery = query(
      itemsCollection,
      where('restaurantId', '==', restaurantId)
    );
    const itemsSnapshot = await getDocs(itemsQuery);

    // Convert to typed objects
    const sections = sectionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as MenuSection[];

    const items = itemsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as MenuItem[];

    // Sort manually by order field
    sections.sort((a, b) => a.order - b.order);
    items.sort((a, b) => a.order - b.order);

    return {
      sections,
      items,
      isPublished: sections.length > 0,
    };
  } catch (error) {
    console.error('Error fetching menu:', error);
    throw new Error('Failed to fetch menu');
  }
};

/**
 * Add a new menu section
 */
export const addSection = async (
  data: MenuSectionFormData & { 
    restaurantId: string; 
    order: number; 
    isVisible: boolean; 
    collapsed: boolean; 
  }
): Promise<MenuSection> => {
  try {
    const sectionData = {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(sectionsCollection, sectionData);
    
    // Return the new section with proper typing
    return {
      id: docRef.id,
      ...data,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  } catch (error) {
    console.error('Error adding section:', error);
    throw new Error('Failed to add section');
  }
};/**
 * Update a menu section
 */
export const updateSection = async (
  id: string, 
  data: Partial<MenuSectionFormData>
): Promise<void> => {
  try {
    const docRef = doc(sectionsCollection, id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating section:', error);
    throw new Error('Failed to update section');
  }
};

/**
 * Delete a menu section and all its items
 */
export const deleteSection = async (id: string): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    // Delete the section
    const sectionRef = doc(sectionsCollection, id);
    batch.delete(sectionRef);
    
    // Delete all items in this section
    const itemsQuery = query(
      itemsCollection,
      where('sectionId', '==', id)
    );
    const itemsSnapshot = await getDocs(itemsQuery);
    
    itemsSnapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error deleting section:', error);
    throw new Error('Failed to delete section');
  }
};

/**
 * Reorder menu sections
 */
export const reorderSections = async (sections: MenuSection[]): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    sections.forEach((section, index) => {
      const sectionRef = doc(sectionsCollection, section.id);
      batch.update(sectionRef, {
        order: index,
        updatedAt: serverTimestamp(),
      });
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error reordering sections:', error);
    throw new Error('Failed to reorder sections');
  }
};

/**
 * Get sections for a restaurant
 */
export const getSections = async (restaurantId: string): Promise<MenuSection[]> => {
  try {
    const sectionsQuery = query(
      sectionsCollection,
      where('restaurantId', '==', restaurantId)
    );
    const snapshot = await getDocs(sectionsQuery);
    
    const sections = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as MenuSection[];

    // Sort manually by order field
    sections.sort((a, b) => a.order - b.order);
    
    return sections;
  } catch (error) {
    console.error('Error fetching sections:', error);
    throw new Error('Failed to fetch sections');
  }
};

/**
 * MENU ITEM OPERATIONS
 */

/**
 * Add a new menu item
 */
export const addItem = async (data: MenuItemCreateData): Promise<MenuItem> => {
  try {
    const itemData = {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(itemsCollection, itemData);
    
    // Return the new item with proper typing
    return {
      id: docRef.id,
      ...data,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  } catch (error) {
    console.error('Error adding item:', error);
    throw new Error('Failed to add item');
  }
};

/**
 * Update a menu item
 */
export const updateItem = async (
  id: string, 
  data: Partial<MenuItemUpdateData>
): Promise<void> => {
  try {
    const docRef = doc(itemsCollection, id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating item:', error);
    throw new Error('Failed to update item');
  }
};

/**
 * Delete a menu item
 */
export const deleteItem = async (id: string): Promise<void> => {
  try {
    const docRef = doc(itemsCollection, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting item:', error);
    throw new Error('Failed to delete item');
  }
};

/**
 * Delete multiple menu items
 */
export const deleteItems = async (ids: string[]): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    ids.forEach((id) => {
      const itemRef = doc(itemsCollection, id);
      batch.delete(itemRef);
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error deleting items:', error);
    throw new Error('Failed to delete items');
  }
};

/**
 * Update availability for multiple items
 */
export const updateItemsAvailability = async (
  ids: string[], 
  isAvailable: boolean
): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    ids.forEach((id) => {
      const itemRef = doc(itemsCollection, id);
      batch.update(itemRef, {
        isAvailable,
        updatedAt: serverTimestamp(),
      });
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error updating items availability:', error);
    throw new Error('Failed to update items availability');
  }
};

/**
 * Reorder menu items within a section
 */
export const reorderItems = async (items: MenuItem[]): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    items.forEach((item, index) => {
      const itemRef = doc(itemsCollection, item.id);
      batch.update(itemRef, {
        order: index,
        updatedAt: serverTimestamp(),
      });
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error reordering items:', error);
    throw new Error('Failed to reorder items');
  }
};

/**
 * Get items for a specific section
 */
export const getItemsBySection = async (sectionId: string): Promise<MenuItem[]> => {
  try {
    const itemsQuery = query(
      itemsCollection,
      where('sectionId', '==', sectionId)
    );
    const snapshot = await getDocs(itemsQuery);
    
    const items = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as MenuItem[];

    // Sort manually by order field
    items.sort((a, b) => a.order - b.order);
    
    return items;
  } catch (error) {
    console.error('Error fetching items by section:', error);
    throw new Error('Failed to fetch items');
  }
};

/**
 * PUBLIC DATA ACCESS (No Authentication Required)
 */

/**
 * Get restaurant data for public viewing (no auth required)
 */
export const getPublicRestaurant = async (restaurantId: string): Promise<Restaurant | null> => {
  try {
    const docRef = doc(db, 'restaurants', restaurantId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      // Only return active restaurants for public viewing
      if (!data.isActive) {
        return null;
      }

      // Check if restaurant owner's access is still valid
      try {
        const { checkUserAccess } = await import('./userProfiles');
        const accessState = await checkUserAccess(data.ownerId);
        
        // If owner's access is expired, don't show the menu publicly
        if (accessState.isExpired) {
          return null;
        }
      } catch (error) {
        console.error('Error checking owner access for public menu:', error);
        // If we can't check access, don't show the menu for security
        return null;
      }
      
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      } as Restaurant;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching public restaurant:', error);
    throw new Error('Failed to fetch restaurant');
  }
};

/**
 * Get public menu data (no auth required) 
 */
export const getPublicMenu = async (restaurantId: string): Promise<MenuData | null> => {
  try {
    // First check if restaurant exists and is active
    const restaurant = await getPublicRestaurant(restaurantId);
    if (!restaurant) {
      return null;
    }

    // Get menu data using existing getMenu function
    return await getMenu(restaurantId);
  } catch (error) {
    console.error('Error fetching public menu:', error);
    throw new Error('Failed to fetch menu');
  }
};